import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronRight, Truck, X } from "lucide-react";
import { get } from "@/lib/api";
import { cn } from "@/lib/utils";
import { fmtDate, todayISO } from "@/lib/format";
import { label, toneOf, TONE_DOT } from "@/lib/labels";
import { LoadingState, ErrorState } from "@/components/common/States";
import { OpsMap, MapMarker, useOpsMap, useMarkerLayout } from "./OpsMap";
import { ModuleGroupMarker } from "./MapMarkers";
import { LayerToggle, MapAttribution, Glass } from "./MapChrome";
import { MapDatePicker } from "./MapDatePicker";
import { layerById, readStoredLayer, storeLayer } from "./mapLayers";
import { boundsOf } from "./mapUtils";

const GroupMarkers = ({ groups, selectedId, onSelect }) => {
  const { zoom } = useOpsMap();
  const points = useMemo(() => groups.map((g) => ({ id: g.location_id, lat: g.lat, lng: g.lng })), [groups]);
  const layout = useMarkerLayout(points, 40);
  return groups.map((g) => {
    const l = layout[g.location_id] || { offset: [0, 0], clusterSize: 1 };
    const selected = selectedId === g.location_id;
    return (
      <MapMarker key={g.location_id} lat={g.lat} lng={g.lng} offset={l.offset} zIndex={selected ? 20 : 10}>
        <ModuleGroupMarker group={g} selected={selected} showLabel={zoom >= 6.4 && l.clusterSize === 1} statusTone={toneOf} onClick={() => onSelect(selected ? null : g.location_id)} />
      </MapMarker>
    );
  });
};

/**
 * Flåte · Kart: every physical module grouped by where it is (depot / project site / in transit). Honours the list
 * filters (status, category, type, search) and can answer for a future date from planned allocations.
 */
export const FleetMap = ({ filters, location, onLocationChange, className }) => {
  const navigate = useNavigate();
  const today = todayISO();
  const [date, setDate] = useState(today);
  const [layer, setLayer] = useState(readStoredLayer);
  const isToday = date === today;
  const changeLayer = (id) => {
    setLayer(id);
    storeLayer(id);
  };
  const params = { date: isToday ? undefined : date, status: filters.status && filters.status !== "all" ? filters.status : undefined, category: filters.category && filters.category !== "all" ? filters.category : undefined, module_type_id: filters.type || undefined, q: filters.q || undefined };
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ["map-modules", params], queryFn: () => get("/map/modules", params), keepPreviousData: true });

  const groups = useMemo(() => (data?.by_location || []).filter((g) => g.kind !== "transit" && g.lat != null), [data]);
  const transit = useMemo(() => (data?.by_location || []).find((g) => g.kind === "transit"), [data]);
  const bounds = useMemo(() => boundsOf(groups), [groups]);
  const selectedGroup = (data?.by_location || []).find((g) => g.location_id === location) || null;
  const items = useMemo(() => {
    const all = data?.items || [];
    if (!location) return all;
    return all.filter((m) => (location === "transit" ? m.location?.kind === "transit" : m.location?.location_id === location));
  }, [data, location]);

  if (isLoading && !data) return <LoadingState className="py-32" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <div className={cn("grid h-[640px] overflow-hidden rounded-xl ring-1 ring-black/[0.06] dark:ring-white/10 lg:grid-cols-[minmax(0,1fr)_360px]", className)} data-testid="fleet-map">
      <OpsMap layer={layer} bounds={bounds} fitPadding={{ top: 72, bottom: 56, left: 64, right: 64 }} maxZoom={11} onBackgroundClick={() => onLocationChange(null)} className="h-[360px] lg:h-full" testId="fleet-ops-map">
        <GroupMarkers groups={groups} selectedId={location} onSelect={onLocationChange} />
        <div className="pointer-events-none absolute left-3 top-3 z-10 flex flex-wrap items-center gap-2">
          <LayerToggle value={layer} onChange={changeLayer} />
          <MapDatePicker date={date} today={today} onChange={(d) => setDate(d || today)} testId="fleet-date-button" />
        </div>
        {!isToday && (
          <div className="pointer-events-none absolute left-3 top-14 z-10">
            <Glass className="flex h-9 items-center gap-2 px-4 text-[12px]" testId="fleet-date-banner">
              <CalendarDays className="h-3.5 w-3.5 text-primary" strokeWidth={1.75} />
              Forventet plassering {fmtDate(date, "d. MMM yyyy")} · basert på planlagte allokeringer
              <button type="button" onClick={() => setDate(today)} className="ml-1 rounded p-0.5 text-muted-foreground hover:text-foreground" aria-label="Tilbake til i dag" data-testid="fleet-date-reset">
                <X className="h-3.5 w-3.5" />
              </button>
            </Glass>
          </div>
        )}
        <div className="pointer-events-none absolute bottom-2 left-3 z-10 rounded-lg bg-card/80 px-2.5 py-1.5 text-[11.5px] text-muted-foreground backdrop-blur" data-testid="fleet-map-hint">
          {data.count} moduler · {groups.length} lokasjoner{transit ? ` · ${transit.count} i transport` : ""} · {isToday ? "Kjent lokasjon, ikke sanntid" : "Forventet ut fra planlegging"}
        </div>
        <MapAttribution layer={layerById(layer)} className="absolute bottom-2 right-2 z-10" />
      </OpsMap>

      <aside className="flex min-h-0 flex-col bg-muted/40 dark:bg-muted/20" data-testid="fleet-map-panel">
        <header className="flex items-start justify-between gap-3 px-4 pt-4">
          <div className="min-w-0">
            <div className="text-[11.5px] text-muted-foreground">{selectedGroup ? (selectedGroup.kind === "depot" ? "Depot" : selectedGroup.kind === "transit" ? "Planlagt transport" : "Prosjekttomt") : "Alle lokasjoner"}</div>
            <h3 className="truncate text-[14px] font-semibold leading-5" data-testid="fleet-panel-title">{selectedGroup ? selectedGroup.name : `${data.count} moduler`}</h3>
          </div>
          {selectedGroup && (
            <button type="button" onClick={() => onLocationChange(null)} aria-label="Vis alle" className="-mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-foreground" data-testid="fleet-panel-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </header>

        {selectedGroup ? (
          <>
            <div className="px-4 pt-3">
              <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-background/80">
                {Object.entries(selectedGroup.by_status).map(([s, n]) => (
                  <div key={s} className={cn("h-full", TONE_DOT[toneOf(s)])} style={{ width: `${(n / selectedGroup.count) * 100}%` }} title={`${label(s)}: ${n}`} />
                ))}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-muted-foreground">
                {Object.entries(selectedGroup.by_status).map(([s, n]) => (
                  <span key={s} className="inline-flex items-center gap-1">
                    <span className={cn("h-1.5 w-1.5 rounded-full", TONE_DOT[toneOf(s)])} /> {label(s)} <span className="tabular">{n}</span>
                  </span>
                ))}
              </div>
            </div>
            <ul className="mt-2 min-h-0 flex-1 space-y-px overflow-y-auto px-2 pb-2 scrollbar-thin" data-testid="fleet-module-list">
              {items.map((m) => (
                <li key={m.id}>
                  <button type="button" onClick={() => navigate(`/moduler/${m.id}`)} className="group grid w-full grid-cols-[auto_1fr_auto] items-center gap-x-3 rounded-lg px-2 py-1.5 text-left transition-colors duration-150 hover:bg-card focus-visible:bg-card focus-visible:outline-none" data-testid={`fleet-module-${m.module_id}`}>
                    <span className={cn("h-1.5 w-1.5 rounded-full", TONE_DOT[toneOf(m.status)])} aria-hidden />
                    <span className="min-w-0 leading-tight">
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono text-[12px] font-medium">{m.module_id}</span>
                        <span className="truncate text-[12px] text-muted-foreground">{m.type_name}</span>
                      </span>
                      <span className="block truncate text-[11.5px] text-muted-foreground">
                        {label(m.status)}
                        {m.location?.position?.slot && ` · ${m.location.position.slot}`}
                        {m.status !== "ledig" && m.available_from && ` · ledig fra ${fmtDate(m.available_from, "d. MMM yyyy")}`}
                        {m.status === "ledig" && m.next_allocation && ` · neste: ${m.next_allocation.project_name?.split(" – ").pop()} ${fmtDate(m.next_allocation.start_date, "d. MMM")}`}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/50 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-foreground" />
                  </button>
                </li>
              ))}
              {items.length === 0 && <li className="px-3 py-8 text-center text-[12.5px] text-muted-foreground">Ingen moduler her med gjeldende filter.</li>}
            </ul>
          </>
        ) : (
          <ul className="mt-3 min-h-0 flex-1 space-y-px overflow-y-auto px-2 pb-2 scrollbar-thin" data-testid="fleet-group-list">
            {(data.by_location || []).map((g) => (
              <li key={g.location_id}>
                <button type="button" onClick={() => onLocationChange(g.location_id)} className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors duration-150 hover:bg-card focus-visible:bg-card focus-visible:outline-none" data-testid={`fleet-group-${g.location_id}`}>
                  <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold tabular", g.kind === "depot" ? "bg-navy text-white" : g.kind === "transit" ? "bg-primary/10 text-primary" : "bg-card text-foreground shadow-[inset_0_0_0_1.5px_hsl(var(--border))]")}>{g.kind === "transit" ? <Truck className="h-3.5 w-3.5" strokeWidth={1.75} /> : g.count}</span>
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate text-[12.5px] font-medium">{g.name}</span>
                    <span className="block truncate text-[11.5px] text-muted-foreground">
                      {g.count} moduler · {Object.entries(g.by_status).map(([s, n]) => `${n} ${label(s).toLowerCase()}`).join(" · ")}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
};
